import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Spinner } from '@economic/taco';
import { t, useLocalization } from '../../../../../../../shared/components/l10n/l10n';
import SelectPlanStep from '../SignupModal/SelectPlanStep';
import { SmartPayIntegrationModel, SMARTPAY_PLAN_OPTIONS } from '../../../types';
import SuccessStep from './SuccessStep';
import ModalFooter from '../ModalFooter/ModalFooter';
import internalApiClient from '../../../../../../../shared/util/api/internalApiClient';
import useQuery from '../../../../../../../shared/hooks/useQuery';
import ErrorStep from './ErrorStep';
import track from '../../../../../../../shared/util/utilities/mixpanel';
import { Module } from '../../../../../../../../types';
import NoPermissionIllustration from '../../../assets/No-permission-Smartpay.svg';

const UPDATE_PLAN_STEPS = {
    SELECT_PLAN: 'SELECT_PLAN',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
};

const ChangePlanDialog = () => {
    const { data: state, loading: stateLoading } = useQuery<SmartPayIntegrationModel>('/smartpay/state');

    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();
    const location = useLocation();
    const flowStepsArray = Object.keys(UPDATE_PLAN_STEPS);

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [initialPlanId, setInitialPlanId] = useState<1 | 2 | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<number>(SMARTPAY_PLAN_OPTIONS[0].id);
    const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
    const [isPermissionLoading, setIsPermissionLoading] = useState(true);
    const [hasUserPermission, sethasUserPermission] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            setIsPermissionLoading(true);
            try {
                const res = await internalApiClient.post(`/modules/${Module.SmartPay}/validateenable`);
                sethasUserPermission(res.data);
            } catch (error) {
                // do nothing
            }
            setIsPermissionLoading(false);
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        if (stateLoading) return;

        const currentPlanId = state.subscriptionType;
        // if the user is not onboarded, aka does not have a plan
        if (currentPlanId === 0) return;

        setInitialPlanId(currentPlanId);
        setSelectedPlanId(currentPlanId);
    }, [stateLoading]);

    const onDialogClose = (hide?: () => void) => {
        if (typeof hide === 'function') hide();
        // remove modal hash
        navigate(location.pathname);
    };

    const onNext = async () => {
        if (selectedPlanId === initialPlanId) {
            setCurrentStepIndex(flowStepsArray.indexOf(UPDATE_PLAN_STEPS.SUCCESS));
            return;
        }

        // update subscription
        setIsUpdatingPlan(true);
        try {
            await internalApiClient.put('/smartpay/subscription', {
                subscriptionType: selectedPlanId,
            });
            setCurrentStepIndex(flowStepsArray.indexOf(UPDATE_PLAN_STEPS.SUCCESS));
        } catch (error) {
            track('smartpay', 'update.modal.error');
            setCurrentStepIndex(flowStepsArray.indexOf(UPDATE_PLAN_STEPS.ERROR));
        }
        setIsUpdatingPlan(false);
    };

    return (
        <Dialog size="xs" onClose={() => onDialogClose()} defaultOpen>
            <Dialog.Content aria-label={t`SMARTPAY_CHANGE_PLAN_MODAL_ACCESSIBILITY_NAME`}>
                {stateLoading || loadingTexts || isUpdatingPlan || isPermissionLoading ? (
                    <div className="flex items-center justify-center h-104">
                        <Spinner />
                    </div>
                ) : !hasUserPermission ? (
                    <div className="h-96 flex flex-col items-center">
                        <img
                            src={NoPermissionIllustration}
                            alt="Smart Pay update plan success graphic"
                            className="w-56 my-6 mx-auto"
                        />
                        <strong>{t`SMARTPAY_CHANGE_PLAN_MODAL_UNAUTHORIZED_USER_MESSAGE`}</strong>
                    </div>
                ) : flowStepsArray[currentStepIndex] === UPDATE_PLAN_STEPS.SELECT_PLAN ? (
                    <SelectPlanStep
                        currentPlan={initialPlanId}
                        status={state.status}
                        lastSubscriptionDate={state.unsubscriptionDate}
                        planState={[selectedPlanId, setSelectedPlanId]}
                        next={() => onNext()}
                        back={() => onDialogClose()}
                    />
                ) : flowStepsArray[currentStepIndex] === UPDATE_PLAN_STEPS.SUCCESS ? (
                    <SuccessStep close={() => onDialogClose()} />
                ) : flowStepsArray[currentStepIndex] === UPDATE_PLAN_STEPS.ERROR ? (
                    <ErrorStep close={() => onDialogClose()} />
                ) : (
                    <></>
                )}
                <ModalFooter />
            </Dialog.Content>
        </Dialog>
    );
};

export default ChangePlanDialog;

// load translations in the modal because this modal is used in market too
const textIds = [
    'SMARTPAY_CHANGE_PLAN_MODAL_ACCESSIBILITY_NAME',
    'SMARTPAY_CHANGE_PLAN_MODAL_TITLE',

    'SMARTPAY_CHANGE_PLAN_MODAL_UNAUTHORIZED_USER_MESSAGE',

    'SMARTPAY_CHANGE_PLAN_MODAL_NO_SUBSCRIPTION_TEXT',
    'SMARTPAY_CHANGE_PLAN_MODAL_WITH_SUBSCRIPTION_TEXT',

    'SMARTPAY_CHANGE_PLAN_MODAL_SUCCESS_TITLE',
    'SMARTPAY_CHANGE_PLAN_MODAL_SUCCESS_MESSAGE',

    'SMARTPAY_CHANGE_PLAN_MODAL_ERROR_TITLE',
    'SMARTPAY_CHANGE_PLAN_MODAL_ERROR_MESSAGE',

    'SMARTPAY_CHANGE_PLAN_MODAL_JOIN_WAITLIST_TITLE',
    'SMARTPAY_CHANGE_PLAN_MODAL_JOIN_WAITLIST_MESSAGE',
    'SMARTPAY_CHANGE_PLAN_MODAL_JOIN_WAITLIST_BUTTON',
    'SMARTPAY_CHANGE_PLAN_MODAL_JOIN_WAITLIST_USER_JOINED',

    'SMARTPAY_ONBOARDING_MODAL_SELECT_PLAN_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_NAME',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_PRICE',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_FEATURES',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_NAME',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_PRICE',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_FEATURES',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_DISCLAIMER',
    'SMARTPAY_CHANGE_PLAN_MODAL_RECENLTY_DOWNGRADED_PLAN_DISCLAIMER',

    'SMARTPAY_CHANGE_PLAN_MODAL_CANCEL_BUTTON',
    'SMARTPAY_CHANGE_PLAN_MODAL_CLOSE_BUTTON',
    'SMARTPAY_CHANGE_PLAN_MODAL_CONTINUE_BUTTON',

    'SMARTPAY_ONBOARDING_MODAL_DELIVERED_BY',
];
